import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { PanelModule } from 'primeng/panel';
import { DividerModule } from 'primeng/divider';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { ChipModule } from 'primeng/chip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { OutreachService } from 'src/app/services/outreach.service';
import { CompaniesService } from 'src/app/services/companies.service';
import { SelectableCompany } from 'src/app/models/company.model';
import { EmailSequence } from 'src/app/models/email-sequence.model';
import { StrategyRouteChipComponent } from 'src/app/components/strategy-route-chip/strategy-route-chip.component';
import { Subject, takeUntil, tap } from 'rxjs';

export interface EmailStepWithOrder {
  stepOrder: number;
  type: string;
  templateId: number | null;
  templateName?: string;
  templateSubject?: string;
  templateBody?: string;
  waitDuration?: number;
  waitUnit?: string;
  abTestingEnabled?: boolean;
  abVariantSubject?: string;
  abVariantBody?: string;
  abVariantCTA?: string;
}

@Component({
  selector: 'app-email-sequence-details',
  templateUrl: './email-sequence-details.component.html',
  styleUrls: ['./email-sequence-details.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    TagModule,
    PanelModule,
    DividerModule,
    SkeletonModule,
    TooltipModule,
    ChipModule,
    ToastModule,
    StrategyRouteChipComponent
  ],
  providers: [MessageService]
})
export class EmailSequenceDetailsComponent implements OnInit, OnDestroy {
  currentCompany: SelectableCompany | null = null;
  sequenceId: string = '';
  companyId: string = '';
  loading: boolean = true;
  sequence: EmailSequence | null = null;
  steps: EmailStepWithOrder[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private outreachService: OutreachService,
    private messageService: MessageService,
    private companiesService: CompaniesService
  ) { }

  ngOnInit() {
    this.sequenceId = this.route.snapshot.params['id'];
    this.companyId = this.route.snapshot.params['companyId'];
    this.setupCompanySubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupCompanySubscription(): void {
    if (this.companyId) {
      const company = this.companiesService.getCompanyById(this.companyId);
      if (company) {
        this.currentCompany = company;
        this.loadSequenceData();
      } else {
        console.warn('Company not found for ID:', this.companyId);
        this.fallbackToSelectedCompany();
      }
    } else {
      this.fallbackToSelectedCompany();
    }
  }

  private fallbackToSelectedCompany(): void {
    this.companiesService.selectedCompany$
      .pipe(
        takeUntil(this.destroy$),
        tap(company => console.log('Company emission:', company))
      )
      .subscribe(company => {
        if (company && company.companyId) {
          if (company.companyId !== this.currentCompany?.companyId) {
            console.log('Company changed from', this.currentCompany?.companyId, 'to', company.companyId);
            this.currentCompany = company;
            this.companyId = company.companyId;
            this.loadSequenceData();
          }
        } else {
          console.log('No valid company received:', company);
          this.currentCompany = null;
        }
      });
  }

  private async loadSequenceData() {
    try {
      this.loading = true;
      // Get sequence details
      const sequence = await this.outreachService.getEmailSequence(this.sequenceId, this.currentCompany!.companyId);
            
      if (!sequence) {
        throw new Error('Sequence not found');
      }
      //@ts-ignore
      this.sequence = sequence.data?.sequence;
      
      // Get sequence steps
      const stepsResult = await this.outreachService.getEmailSequenceSteps(this.sequenceId, this.currentCompany!.companyId);
      //@ts-ignore
      if (Array.isArray(stepsResult.data)) {
        // Add stepOrder to each step and sort
        //@ts-ignore
        this.steps = stepsResult.data.map((step: any, index: number) => ({
          ...step,
          stepOrder: step.stepOrder || (index + 1)
        })).sort((a: any, b: any) => a.stepOrder - b.stepOrder);
      } else {
        this.steps = [];
      }
      
      this.loading = false;
    } catch (error) {
      console.error('Error loading sequence data:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load sequence details'
      });
      this.loading = false;
    }
  }

  goBack() {
    this.router.navigate(['/email-outreach']);
  }

  pauseSequence() {
    if (this.sequence) {
      console.log('Pausing sequence:', this.sequence.name);
      // TODO: Implement pause sequence logic
      this.messageService.add({
        severity: 'info',
        summary: 'Feature Coming Soon',
        detail: 'Pause sequence functionality will be available soon'
      });
    }
  }

  startSequence() {
    if (this.sequence) {
      console.log('Starting sequence:', this.sequence.name);
      // TODO: Implement start sequence logic
      this.messageService.add({
        severity: 'info',
        summary: 'Feature Coming Soon',
        detail: 'Start sequence functionality will be available soon'
      });
    }
  }

  getStatusSeverity(): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (!this.sequence) return 'info';
    switch (this.sequence.status) {
      case 'active': return 'success';
      case 'paused': return 'warn';
      case 'draft': return 'info';
      case 'completed': return 'secondary';
      default: return 'info';
    }
  }

  getStepIcon(step: EmailStepWithOrder): string {
    switch (step.type) {
      case 'initial': return 'pi pi-envelope';
      case 'followup1':
      case 'followup2':
      case 'followup3':
      default: return 'pi pi-reply';
    }
  }

  getStepTitle(step: EmailStepWithOrder): string {
    switch (step.type) {
      case 'initial': return 'Initial Email';
      case 'followup1': return 'Follow-up #1';
      case 'followup2': return 'Follow-up #2';
      case 'followup3': return 'Follow-up #3';
      default: return `Follow-up #${step.stepOrder}`;
    }
  }

  getWaitText(step: EmailStepWithOrder): string {
    if (!step.waitDuration || step.waitDuration === 0) {
      return 'Wait: Immediately';
    }
    return `Wait: ${step.waitDuration} ${step.waitUnit}`;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(value);
  }

  capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  getActionButtonConfig() {
    if (!this.sequence) return { label: 'Start', icon: 'pi pi-play', action: () => this.startSequence() };
    
    switch (this.sequence.status) {
      case 'active':
        return { label: 'Pause', icon: 'pi pi-pause', action: () => this.pauseSequence() };
      case 'paused':
        return { label: 'Resume', icon: 'pi pi-play', action: () => this.startSequence() };
      case 'draft':
        return { label: 'Start', icon: 'pi pi-play', action: () => this.startSequence() };
      default:
        return { label: 'Start', icon: 'pi pi-play', action: () => this.startSequence() };
    }
  }
}